/**
 * Single source of truth for the telemetry PHI/secret deny-list, the safe-to-ship allow-list, and the
 * PACE/PHI identifier key list. Imported by BOTH the server (server/lib/phi-redact.ts) and the client
 * (client/lib/phi-redact.ts) scrubbers so the two tiers can NEVER diverge (B-L3). Pure data only — no
 * DOM/Node deps — so it is safe in either build graph.
 *
 * HIPAA note: these lists are the LAST line of defense behind call-site discipline. The allow-list is
 * fail-safe (a newly introduced field is excluded by default); the deny-list explicitly names known
 * secret + PHI + FHIR/clinical field names so a stray copy is dropped even if it slips into a property.
 */

/** PACE/PHI identifier keys that must never appear in cleartext logs (matched case-insensitively). */
export const PHI_KEYS = [
  "first_name",
  "last_name",
  "name",
  "mrn",
  "date_of_birth",
  "dob",
  "medicare_id",
  "medicaid_id",
  "participant_id",
  "member_id",
  "fhir_id",
  "npi",
  "facility_npi",
  "ssn",
  "address",
  "email",
  "phone",
] as const;

/**
 * Allow-list of keys that are SAFE to ship as telemetry custom dimensions. Anything NOT in this set is
 * dropped by the allow-list gate (fail-safe: a newly introduced PHI field is excluded by default). Only
 * operational / correlation / pagination identifiers belong here — NEVER a participant-level field.
 */
export const SAFE_LOG_KEYS = [
  // correlation / tracing
  "traceId",
  "correlationId",
  "spanId",
  "sessionId",
  // request shape
  "method",
  "path",
  "route",
  "status",
  "statusCode",
  "ms",
  "duration",
  "durationMs",
  // pagination / counts
  "count",
  "page",
  "pageSize",
  "total",
  // domain (non-PHI) reference ids
  "measureId",
  "centerId",
  "providerId",
  "code",
  // log envelope / operational
  "level",
  "component",
  "appName",
  "environment",
  "timestamp",
  "operation",
  "operationId",
  "event",
  "action",
  "success",
  "result",
  "reason_code",
  // auth (non-PHI): RBAC role name. NOTE: `subject` and `reason` are deliberately NOT allow-listed —
  // both collide with FHIR fields (Observation.subject is a Patient ref; a clinical `reason` is PHI)
  // and `reason` is also in the deny-list, so allow-listing them would erode the fail-safe in a
  // PHI-heavy codebase. Auth telemetry logs its rejection slug under `result`, not `reason`.
  "role",
  // infra / multi-tenant routing (non-PHI)
  "userId",
  "tenant",
  "instance",
  "host",
  "port",
  "database",
  "dir",
  "expiresInSeconds",
  // global error handler frame metadata (file path / position — non-PHI)
  "source",
  "line",
  "column",
  "type",
] as const;

/**
 * Deny-list of property/header names that must NEVER reach App Insights — secrets (auth/cookies/keys/
 * connection strings/tokens), healthcare PHI (name/MRN/DOB/medicare ids/participant ids/address/email/
 * phone/SSN/NPI) and FHIR/clinical field names (birthDate/given/family/telecom/identifier/gender/
 * diagnosis/condition/note/chief_complaint/reason). Lower-cased for case-insensitive matching.
 */
export const DENY_PROP_KEYS_LIST = [
  // secrets / auth
  "authorization",
  "proxy-authorization",
  "cookie",
  "set-cookie",
  "x-api-key",
  "x-auth-token",
  "api-key",
  "apikey",
  "password",
  "secret",
  "connectionstring",
  "connection_string",
  "token",
  "accesstoken",
  "refreshtoken",
  // healthcare PHI / PII
  "ssn",
  "mrn",
  "dob",
  "date_of_birth",
  "name",
  "first_name",
  "last_name",
  "address",
  "email",
  "phone",
  "medicare_id",
  "medicaid_id",
  "participant_id",
  "member_id",
  "fhir_id",
  "npi",
  "facility_npi",
  // FHIR / clinical field names (H1 — deny-list previously missed these)
  "birthdate",
  "given",
  "family",
  "telecom",
  "identifier",
  "gender",
  "diagnosis",
  "condition",
  "note",
  "chief_complaint",
  "reason",
] as const;

/** Lower-cased Set form of the deny-list for O(1) case-insensitive membership tests. */
export const DENY_PROP_KEYS = new Set<string>(DENY_PROP_KEYS_LIST.map((k) => k.toLowerCase()));

/** Lower-cased Set form of the PHI identifier keys. */
export const PHI_KEY_SET = new Set<string>(PHI_KEYS.map((k) => k.toLowerCase()));

/** Lower-cased Set form of the safe allow-list. */
export const SAFE_LOG_KEY_SET = new Set<string>(SAFE_LOG_KEYS.map((k) => k.toLowerCase()));
